<script lang="ts">
  /**
   * Director home screen — literal port of design A mockup.
   * Per memory `feedback_v2_literal_port`: markup matches the mockup;
   * data is wired to /api/digest/today.
   *
   * Fallback: in browser-preview mode (no TG initData → 401 from API),
   * we show the mockup hardcoded data so the design can be QA'd offline.
   */
  import { onMount } from "svelte";
  import { api, type AlertItem, type Digest, type Kpi, type ManagerSummary } from "../lib/api";
  import { isInTelegram } from "../lib/tg";
  import type { CurrentUser } from "../lib/state.svelte";

  let { user }: { user: CurrentUser } = $props();

  let digest = $state<Digest | null>(null);
  let loading = $state(true);
  let loadError = $state<string | null>(null);
  let preview = $state(false);
  let myTasksTotal = $state(0);
  let callsMissed = $state(0);

  // Mockup data — used when no auth (browser preview)
  const PREVIEW: Digest = {
    today_ru: "Понедельник, 18 мая",
    hero_count: 12,
    hero_bullets: [
      "4 просрочки у менеджеров",
      "3 сделки молчат больше 7 дней",
      "2 договора без аванса 5+ дней",
      "3 замера на этой неделе",
    ],
    kpi: [
      { label: "в воронке", value: "4.28", unit: "млн ₽", delta_text: "+320к за неделю", delta_positive: true, tone: "accent" },
      { label: "открытых", value: "12", unit: "", delta_text: "средний возраст 18 дн", delta_positive: false, tone: "default" },
      { label: "замеры", value: "3", unit: "", delta_text: "на этой неделе", delta_positive: false, tone: "warn" },
      { label: "просрочки", value: "7", unit: "", delta_text: "требуют внимания", delta_positive: false, tone: "danger" },
    ],
    managers: [
      { amo_id: 9189826, name: "Даниил Тришин", deals_count: 5, open_tasks: 4, overdue: 2, silent_long: 1, tone: "warn", status_text: "2 просрочки · 1 сделка молчит 9 дн" },
      { amo_id: 8415547, name: "Александр Ушанев", deals_count: 7, open_tasks: 3, overdue: 0, silent_long: 0, tone: "ok", status_text: "всё в норме · 2 замера на неделе" },
    ],
    alerts: [
      { lead_amo_id: 1, deal_name: "Петров В.А. — Камин", vip: true, price_rub: 1_200_000, manager_name: "Даниил", stage_name: "Дожим вдолгую", age_days: 9, reason: "Молчит дольше всех в этой стадии", tone: "danger" },
      { lead_amo_id: 2, deal_name: "Иванова И.И. — Камин-сауна", vip: false, price_rub: 800_000, manager_name: "Даниил", stage_name: "Договор/оплаты", age_days: 5, reason: "Договор подписан, аванса нет", tone: "danger" },
      { lead_amo_id: 3, deal_name: "Карина — Камин Brunner", vip: false, price_rub: 2_100_000, manager_name: "Ушанев", stage_name: "Дожим", age_days: 6, reason: "Отправлено итоговое КП, нет ответа", tone: "warn" },
    ],
    alerts_total: 7,
  };

  function formatPrice(rub: number): string {
    if (rub >= 1_000_000) return `${(rub / 1_000_000).toFixed(1).replace(/\.0$/, "")}М ₽`;
    if (rub >= 1_000) return `${Math.floor(rub / 1000)}К ₽`;
    return `${rub} ₽`;
  }

  function ageText(days: number): string {
    if (days <= 0) return "сегодня";
    if (days === 1) return "1 день";
    if (days < 5) return `${days} дня`;
    return `${days} дней`;
  }

  // KPI tile → route mapping
  function kpiHref(label: string): string {
    switch (label) {
      case "в воронке": return "?view=analytics";
      case "открытых":  return "?view=deals";
      case "замеры":    return "?view=deals&stage=measure";
      case "просрочки": return "?view=tasks&filter=overdue";
      default: return "?view=analytics";
    }
  }

  async function loadCallsMissed() {
    try {
      callsMissed = (await api.callsStats(2)).not_called_back;
    } catch {
      callsMissed = 0;
    }
  }

  async function loadMyTaskCount() {
    try {
      const r = await api.myTasks("today_tomorrow");
      myTasksTotal = r.total;
    } catch {
      myTasksTotal = 0;
    }
  }

  onMount(async () => {
    if (!isInTelegram()) {
      preview = true;
      digest = PREVIEW;
      loading = false;
      return;
    }

    // Resolve role first — non-directors don't get the director digest:
    //   manager   → their own ManagerView (deals + tasks)
    //   installer → personal task list (tech-cards); director tiles/voice stay hidden
    try {
      const me = await api.me();
      if (me.role === "manager" && me.amo_user_amo_id) {
        history.replaceState(null, "", `?manager=${me.amo_user_amo_id}`);
        location.reload();  // App.svelte will pick up new route
        return;
      }
      if (me.role === "installer") {
        history.replaceState(null, "", `?view=installer`);
        location.reload();
        return;
      }
      if (me.role === "unknown" || me.role === "integration") {
        history.replaceState(null, "", `?view=my-tasks&range=all`);
        location.reload();
        return;
      }
    } catch (e) {
      // ignore — fall through to digest fetch / preview
    }

    try {
      digest = await api.digest();
    } catch (e) {
      loadError = e instanceof Error ? e.message : "load failed";
      // Do NOT fall back to fabricated PREVIEW data inside Telegram — a director
      // glancing at fake KPIs/alerts is worse than an honest error. Show retry.
    } finally {
      loading = false;
    }

    // Fetch own task count for the «Мои задачи» tile badge
    loadMyTaskCount();
    loadCallsMissed();
  });

  async function retryLoad() {
    loadError = null;
    loading = true;
    try {
      digest = await api.digest();
    } catch (e) {
      loadError = e instanceof Error ? e.message : "load failed";
    } finally {
      loading = false;
    }
    loadMyTaskCount();
    loadCallsMissed();
  }

  const initials = $derived((user.firstName?.[0] ?? "?") + (user.lastName?.[0] ?? ""));
</script>

<div class="screen">
  <header class="tg-header">
    <div>
      <h1>Привет, <span class="accent">{user.firstName}</span></h1>
      <div class="date">
        {digest?.today_ru ?? ""}
        {#if preview}<span class="preview-tag">PREVIEW</span>{/if}
        {#if loadError}<span class="error-tag">offline</span>{/if}
      </div>
    </div>
    <div class="hdr-actions">
      <button class="search-btn"
              onclick={() => window.dispatchEvent(new Event("open-search"))}
              aria-label="Поиск">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="7"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
      </button>
      <div class="avatar">
        {#if user.photoUrl}
          <img src={user.photoUrl} alt={user.firstName} />
        {:else}
          {initials}
        {/if}
      </div>
    </div>
  </header>

  {#if loading}
    <div class="loading">Загружаю свежие данные…</div>
  {:else if loadError && !preview}
    <div class="load-error">
      <div class="err-title">Не удалось загрузить данные</div>
      <div class="err-msg">{loadError}</div>
      <button class="retry-btn" onclick={retryLoad}>Повторить</button>
    </div>
  {:else if !digest}
    <div class="loading">Загружаю свежие данные…</div>
  {:else}
    <section class="hero-alert">
      <h2>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z"/>
        </svg>
        Что горит сегодня
      </h2>
      <div class="count">{digest.hero_count} <span class="count-unit">алертов</span></div>
      <ul>
        {#each digest.hero_bullets as line}
          <li>{line}</li>
        {/each}
      </ul>
    </section>

    <a class="dozhim-tile" href="?view=deals&stage=nudge_long">
      <svg class="dozhim-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z"/>
      </svg>
      <div class="dozhim-text">
        <div class="dozhim-label">Дожим вдолгую</div>
        <div class="dozhim-hint">сделки, которые молчат — не дай им умереть</div>
      </div>
      <div class="dozhim-chev">›</div>
    </a>

    {#if !preview}
      <div class="quick-row">
        <button class="new-deal" type="button"
                onclick={() => window.dispatchEvent(new Event("open-deal-create"))}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
          Новая сделка
        </button>
        <a class="quick-tile" href="?view=my-deals&sort=new">
          <svg class="quick-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="7" width="18" height="13" rx="2"/>
            <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
          <div class="quick-text">
            <div class="quick-label">Мои сделки</div>
            <div class="quick-hint">{myTasksTotal ? "новые сверху" : "сортировка и фильтры"}</div>
          </div>
        </a>
        <a class="quick-tile calls-tile" href={callsMissed ? "?view=calls&filter=not_called_back" : "?view=calls"}>
          <svg class="quick-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.4 1.8.7 2.7a2 2 0 01-.5 2.1L8 9.8a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.7.7a2 2 0 011.7 2z"/>
          </svg>
          <div class="quick-text">
            <div class="quick-label">Звонки</div>
            <div class="quick-hint" class:alarm={callsMissed > 0}>
              {callsMissed ? `не перезвонили: ${callsMissed}` : "расшифровки, оценки, пропущенные"}
            </div>
          </div>
        </a>
        <a class="quick-tile" href="?view=my-tasks&range=today_tomorrow">
          <svg class="quick-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <polyline points="12 7 12 12 15 14"/>
          </svg>
          <div class="quick-text">
            <div class="quick-label">Мои задачи {myTasksTotal ? `· ${myTasksTotal}` : ""}</div>
            <div class="quick-hint">сегодня и завтра</div>
          </div>
        </a>
      </div>
    {/if}

    <section class="kpi-grid">
      {#each digest.kpi as k}
        {@const href = kpiHref(k.label)}
        <a class="kpi {k.tone}" href={href}>
          <div class="label">{k.label}</div>
          <div class="value">
            {k.value}{#if k.unit}<span class="unit">{k.unit}</span>{/if}
          </div>
          <div class="delta" class:positive={k.delta_positive}>
            {#if k.delta_positive}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M7 14l5-5 5 5z"/></svg>
            {/if}
            {k.delta_text}
          </div>
        </a>
      {/each}
    </section>

    {#if digest.managers.length}
      <div class="section-title">
        Менеджеры
        <a class="link" href="?view=analytics">аналитика →</a>
      </div>
      <section class="mgr-list">
        {#each digest.managers as m}
          <a class="mgr {m.tone}" href={`?manager=${m.amo_id}`}>
            <div class="light-dot"></div>
            <div class="info">
              <div class="name">
                {m.name}
                <span class="count">{m.deals_count} сделок</span>
              </div>
              <div class="status">{m.status_text}</div>
            </div>
            <div class="chev">›</div>
          </a>
        {/each}
      </section>
    {/if}

    <div class="section-title">
      Горят
      <a class="link" href="?view=alerts">все {digest.alerts_total} →</a>
    </div>

    <section class="alerts">
      {#if digest.alerts.length === 0}
        <div class="empty">Критичных алертов нет</div>
      {:else}
        {#each digest.alerts as a}
          <a class="alert {a.tone}" href={`?deal=${a.lead_amo_id}`}>
            <div class="top">
              <div class="deal-name">
                {a.deal_name}{#if a.vip}<span class="vip-mark">VIP</span>{/if}
              </div>
              <div class="deal-price">{formatPrice(a.price_rub)}</div>
            </div>
            <div class="meta">
              {a.manager_name} <span class="sep"></span> {a.stage_name} <span class="sep"></span> {ageText(a.age_days)}
            </div>
            <div class="reason">{a.reason}</div>
          </a>
        {/each}
      {/if}
    </section>
  {/if}
</div>

<style>
  .screen { padding-bottom: 100px; }

  /* TG header */
  .tg-header {
    padding: calc(12px + var(--safe-top, 0px)) 20px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--border);
  }
  .tg-header h1 {
    font-family: 'Manrope', sans-serif;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
  .tg-header h1 .accent { color: var(--ember); }
  .date {
    font-size: 12px;
    color: var(--text-mute);
    font-weight: 500;
    margin-top: 2px;
  }
  .hdr-actions { display: flex; align-items: center; gap: 8px; }
  .search-btn {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    color: var(--text);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: all 120ms;
  }
  .search-btn:active { transform: scale(0.94); background: var(--bg-elev-2); }
  .search-btn svg { width: 18px; height: 18px; }
  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--bg-elev-2);
    border: 1px solid var(--brass);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Manrope', sans-serif;
    font-weight: 700;
    font-size: 13px;
    color: var(--brass);
    letter-spacing: 0.02em;
    text-transform: uppercase;
    overflow: hidden;
  }
  .avatar img { width: 100%; height: 100%; object-fit: cover; }

  /* Hero alert */
  .hero-alert {
    margin: 16px 16px 0;
    padding: 16px 18px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-left: 3px solid var(--ember);
    border-radius: 14px;
    box-shadow: var(--shadow-sm);
  }
  .hero-alert h2 {
    font-family: 'Manrope', sans-serif;
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .hero-alert h2 svg { width: 18px; height: 18px; color: var(--ember); }
  .count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 28px;
    font-weight: 700;
    color: var(--ember);
    margin: 4px 0 10px;
    letter-spacing: -0.02em;
  }
  .count-unit {
    font-size: 14px;
    color: var(--text-mute);
    font-weight: 500;
  }
  .hero-alert ul {
    list-style: none;
    font-size: 13px;
    color: var(--text-mute);
  }
  .hero-alert li {
    padding: 4px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .hero-alert li::before {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--ember);
    opacity: 0.7;
  }

  /* KPI grid */
  .kpi-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 14px 16px;
  }
  .kpi {
    display: block;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 14px;
    box-shadow: var(--shadow-sm);
    color: var(--text);
    text-decoration: none;
    transition: transform 80ms ease;
  }
  .kpi:active { transform: scale(0.985); }
  .kpi .label {
    font-size: 11px;
    color: var(--text-dim);
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .kpi .value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 22px;
    font-weight: 700;
    margin-top: 4px;
    color: var(--text);
    letter-spacing: -0.02em;
  }
  .kpi .value .unit {
    font-size: 14px;
    color: var(--text-mute);
    margin-left: 3px;
    font-weight: 500;
  }
  .kpi .delta {
    font-size: 11px;
    color: var(--text-mute);
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
  }
  .kpi .delta.positive { color: var(--success); }
  .kpi.warn .value { color: var(--warning); }
  .kpi.danger .value { color: var(--danger); }
  .kpi.danger .delta { color: var(--danger); }
  .kpi.accent .value { color: var(--brass); }

  /* Section title */
  .section-title {
    padding: 18px 20px 8px;
    font-family: 'Manrope', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-dim);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .section-title .link {
    color: var(--ember);
    font-size: 11px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
  }

  /* Quick row: my-deals + all-tasks shortcuts */
  .quick-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 14px 16px 4px;
  }
  .quick-tile {
    display: flex; align-items: center; gap: 12px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-left: 3px solid var(--brass);
    border-radius: 14px;
    padding: 14px;
    box-shadow: var(--shadow-sm);
    color: var(--text);
    text-decoration: none;
    transition: transform 80ms ease;
  }
  .quick-tile:active { transform: scale(0.985); }
  .new-deal {
    grid-column: 1 / -1;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    height: 46px;
    background: var(--ember); color: #fff;
    border: none; border-radius: 14px;
    font-family: 'Manrope', sans-serif; font-weight: 700; font-size: 14px;
    box-shadow: var(--shadow-sm);
    cursor: pointer;
    transition: transform 120ms ease;
  }
  .new-deal svg { width: 18px; height: 18px; }
  .new-deal:active { transform: scale(0.97); }
  .calls-tile { grid-column: 1 / -1; order: 3; }
  .quick-hint.alarm { color: var(--danger); font-weight: 600; }
  .quick-icon {
    width: 24px; height: 24px;
    color: var(--brass);
    flex-shrink: 0;
  }
  .quick-text { min-width: 0; }
  .quick-label {
    font-family: 'Manrope', sans-serif;
    font-weight: 700; font-size: 14px;
  }
  .quick-hint {
    font-size: 11px;
    color: var(--text-mute);
    margin-top: 2px;
  }


  /* Managers list */
  .mgr-list { padding: 0 16px; display: flex; flex-direction: column; gap: 8px; }
  .mgr {
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 12px 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: var(--shadow-sm);
    color: var(--text);
    text-decoration: none;
    transition: transform 80ms ease;
  }
  .mgr:active { transform: scale(0.985); }
  .mgr .chev { color: var(--text-dim); font-size: 20px; line-height: 1; }
  .mgr .light-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .mgr.warn .light-dot { background: var(--warning); }
  .mgr.ok .light-dot { background: var(--success); }
  .mgr.danger .light-dot { background: var(--danger); }
  .mgr .info { flex: 1; min-width: 0; }
  .mgr .name {
    font-weight: 600;
    font-size: 14px;
    font-family: 'Manrope', sans-serif;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .mgr .name .count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: var(--text-mute);
    font-weight: 600;
  }
  .mgr .status {
    font-size: 11px;
    color: var(--text-mute);
    margin-top: 3px;
  }
  .mgr.warn .status { color: var(--warning); }
  .mgr.danger .status { color: var(--danger); }

  .loading {
    padding: 40px 16px;
    text-align: center;
    color: var(--text-mute);
    font-size: 13px;
  }
  .empty {
    padding: 24px 16px;
    text-align: center;
    color: var(--text-mute);
    font-size: 14px;
    background: var(--bg-elev);
    border: 1px dashed var(--border);
    border-radius: 14px;
  }
  .preview-tag, .error-tag {
    display: inline-block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 999px;
    margin-left: 6px;
    letter-spacing: 0.05em;
    vertical-align: middle;
  }
  .preview-tag {
    background: var(--bg-elev-2);
    color: var(--text-dim);
    border: 1px solid var(--border);
  }
  .error-tag {
    background: var(--bg-elev-2);
    color: var(--warning);
    border: 1px solid var(--warning);
  }

  /* Load error (director must not see fabricated data) */
  .load-error {
    margin: 40px 16px;
    padding: 24px 18px;
    text-align: center;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 14px;
  }
  .load-error .err-title {
    font-family: 'Manrope', sans-serif;
    font-weight: 700;
    font-size: 15px;
    color: var(--text);
  }
  .load-error .err-msg {
    font-size: 12px;
    color: var(--text-mute);
    margin-top: 6px;
    word-break: break-word;
  }
  .retry-btn {
    margin-top: 16px;
    padding: 10px 22px;
    border-radius: 999px;
    background: var(--ember);
    color: #fff;
    border: none;
    font-family: 'Manrope', sans-serif;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    transition: transform 80ms ease;
  }
  .retry-btn:active { transform: scale(0.96); }

  /* Дожим вдолгую — entry point to the KEY long-tail pain */
  .dozhim-tile {
    margin: 12px 16px 0;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-left: 3px solid var(--ember);
    border-radius: 14px;
    box-shadow: var(--shadow-sm);
    color: var(--text);
    text-decoration: none;
    transition: transform 80ms ease;
  }
  .dozhim-tile:active { transform: scale(0.985); }
  .dozhim-icon { width: 22px; height: 22px; color: var(--ember); flex-shrink: 0; }
  .dozhim-text { flex: 1; min-width: 0; }
  .dozhim-label {
    font-family: 'Manrope', sans-serif;
    font-weight: 700;
    font-size: 14px;
  }
  .dozhim-hint {
    font-size: 11px;
    color: var(--text-mute);
    margin-top: 2px;
  }
  .dozhim-chev { color: var(--text-dim); font-size: 20px; line-height: 1; }

  /* Alerts list */
  .alerts { padding: 4px 16px; display: flex; flex-direction: column; gap: 8px; }
  .alert {
    display: block;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 12px 14px;
    box-shadow: var(--shadow-sm);
    position: relative;
    overflow: hidden;
    color: var(--text);
    text-decoration: none;
    transition: transform 80ms ease;
  }
  .alert:active { transform: scale(0.98); }
  .alert::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: var(--danger);
  }
  .alert.warn::before { background: var(--warning); }
  .alert.vip::before { background: var(--vip); }

  .alert .top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
  }
  .alert .deal-name {
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    font-weight: 600;
  }
  .alert .deal-name .vip-mark {
    color: var(--vip);
    font-size: 10px;
    margin-left: 4px;
    vertical-align: middle;
  }
  .alert .deal-price {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 600;
    color: var(--brass);
  }
  .alert .meta {
    font-size: 11px;
    color: var(--text-mute);
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .alert .meta .sep {
    width: 3px;
    height: 3px;
    background: var(--text-dim);
    border-radius: 50%;
  }
  .alert .reason {
    font-size: 11px;
    color: var(--danger);
    margin-top: 4px;
    font-weight: 500;
  }
  .alert.warn .reason { color: var(--warning); }
</style>
